import React, { useState, useEffect } from 'react';
import './BrandSignIn.css'; // Ensure the styles are imported
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

// Import Firebase authentication methods
import { signInWithEmailAndPassword, signInWithPopup, signOut as firebaseSignOut, createUserWithEmailAndPassword } from 'firebase/auth';
import { googleProvider, auth, getUserData } from './firebase'; // Ensure the Firebase configuration is correctly imported
import { db } from './firebase';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import BrandSignInTokenService from './services/brandSignInTokenService';
import { checkInviteEmailAccountType } from './services/brandInviteEmailService';
import {
  getAccountTypeMessage,
  getDenialMessageFromUserData,
  shouldRedirectToCreatorDashboard,
  isBrandDashboardAllowedUserType,
  getCreatorDashboardSignInUrl,
} from './utils/accountTypeMessages';

const BrandSignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [tokenInfo, setTokenInfo] = useState(null);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const clearError = () => {
    setError('');
  };

  // Check for token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      validateToken(token);
    }
  }, [searchParams]);

  useEffect(() => {
    const notice = sessionStorage.getItem('brandSignInNotice');
    if (notice) {
      setError(notice);
      sessionStorage.removeItem('brandSignInNotice');
    }
  }, []);

  const validateToken = async (token) => {
    try {
      const result = await BrandSignInTokenService.validateToken(token);
      if (result.valid) {
        setTokenInfo({ ...result.data, tokenType: result.tokenType });
        // Pre-fill email if available
        if (result.data.brandEmail) {
          setEmail(result.data.brandEmail);
        }
        
        // If it's a sign-up token, automatically set to create account mode
        if (result.tokenType === 'signup') {
          setIsCreatingAccount(true);
        } else {
          // For sign-in tokens, check if account exists
          await checkIfAccountExists(result.data.brandEmail);
        }
      } else {
        setError(result.error || 'Invalid or expired link');
      }
    } catch (err) {
      console.error('Error validating token:', err);
      setError('Failed to validate link');
    }
  };

  const checkIfAccountExists = async (email) => {
    try {
      // Try to get user data from Firestore to see if they have an account
      // We'll check if there's a user document with this email
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        // No account found - show create account option
        setIsCreatingAccount(true);
      } else {
        // Account exists in Firestore, but they might not have Firebase Auth yet
        // We'll let them try to sign in first, and if it fails, show create account
        setIsCreatingAccount(false);
      }
    } catch (err) {
      console.error('Error checking account:', err);
      // Default to sign-in mode
      setIsCreatingAccount(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return { valid: false, error: 'Password must be at least 8 characters' };
    }
    if (!/[A-Z]/.test(password)) {
      return { valid: false, error: 'Password must contain at least one capital letter' };
    }
    if (!/[a-z]/.test(password)) {
      return { valid: false, error: 'Password must contain at least one lowercase letter' };
    }
    if (!/[0-9]/.test(password)) {
      return { valid: false, error: 'Password must contain at least one number' };
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return { valid: false, error: 'Password must contain at least one special character (!@#$%^&* etc.)' };
    }
    return { valid: true, error: null };
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    clearError();
    
    // Validate email format
    if (!email || !validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      let userCredential;
      
      // If creating account, use createUserWithEmailAndPassword
      if (isCreatingAccount) {
        // Validate password match
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          return;
        }
        
        // Validate password strength
        const passwordValidation = validatePassword(password);
        if (!passwordValidation.valid) {
          setError(passwordValidation.error);
          return;
        }
        
        // Validate token matches email (if token exists)
        if (tokenInfo && tokenInfo.brandEmail && tokenInfo.brandEmail.toLowerCase() !== email.toLowerCase()) {
          setError(`This sign-up link is for ${tokenInfo.brandEmail}. Please use the correct email address.`);
          return;
        }
        
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
        
        // Create or link Firestore document
        try {
          const userDocRef = doc(db, 'users', userCredential.user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (tokenInfo && tokenInfo.tokenType === 'signup') {
            // For sign-up tokens, create or update brand user document
            if (!userDoc.exists()) {
              // Create new document
              await setDoc(userDocRef, {
                email: email,
                brandName: tokenInfo.brandName || '',
                brandEmail: tokenInfo.brandEmail || email,
                userType: 'client',
                credits: 0,
                walletBalance: 0,
                createdAt: new Date().toISOString()
              });
            } else {
              // Update existing document with token info if needed
              const existingData = userDoc.data();
              await setDoc(userDocRef, {
                email: email,
                brandName: tokenInfo.brandName || existingData.brandName || '',
                brandEmail: tokenInfo.brandEmail || email,
                userType: existingData.userType || 'client',
                credits: existingData.credits || 0,
                walletBalance: existingData.walletBalance || 0,
                updatedAt: new Date().toISOString()
              }, { merge: true });
            }
          } else if (tokenInfo && tokenInfo.brandUserId) {
            // For sign-in tokens, link to existing document
            const userRef = doc(db, 'users', tokenInfo.brandUserId);
            const userDoc = await getDoc(userRef);
            
            if (userDoc.exists()) {
              await setDoc(userRef, {
                email: email,
                firebaseAuthUid: userCredential.user.uid,
                updatedAt: new Date().toISOString()
              }, { merge: true });
            } else {
              // Create new user document if it doesn't exist
              await setDoc(doc(db, 'users', userCredential.user.uid), {
                email: email,
                brandName: tokenInfo.brandName || '',
                userType: 'client',
                createdAt: new Date().toISOString()
              });
            }
          } else {
            // No token info - check if document exists, create if not
            if (!userDoc.exists()) {
              await setDoc(userDocRef, {
                email: email,
                userType: 'client',
                credits: 0,
                walletBalance: 0,
                createdAt: new Date().toISOString()
              });
            }
          }
        } catch (firestoreErr) {
          console.error('Error creating/linking Firestore document:', firestoreErr);
          // Continue anyway - the account is created
        }
      } else {
        // Try to sign in
        userCredential = await signInWithEmailAndPassword(auth, email, password);
      }
      
      // If token was used, mark it as used
      const token = searchParams.get('token');
      if (token && tokenInfo) {
        const tokenType = tokenInfo.tokenType || 'signin';
        await BrandSignInTokenService.markTokenAsUsed(token, tokenType);
      }
      
      const userData = await getUserData(userCredential.user.uid);
      console.log('Brand SignIn: User data from Firestore:', userData);
      
      if (userData && shouldRedirectToCreatorDashboard(userData.userType)) {
        console.log('Brand SignIn: User is a creator, redirecting to creator dashboard');
        setError(getDenialMessageFromUserData(userData));
        await firebaseSignOut(auth);
        window.location.href = getCreatorDashboardSignInUrl();
        return;
      }

      if (userData && !isBrandDashboardAllowedUserType(userData.userType)) {
        console.log('Brand SignIn: Account type not allowed:', userData.userType);
        setError(getDenialMessageFromUserData(userData));
        await firebaseSignOut(auth);
        return;
      }

      console.log('Brand SignIn: User allowed for brand dashboard');
      navigate('/dashboard');
      
      // Redirect or perform any other action after successful login
    } catch (err) {
      console.error('Brand SignIn: Email login failed:', err);
      
      // If sign-in fails and we have a token, offer to create account
      if (!isCreatingAccount && tokenInfo && err.code === 'auth/user-not-found') {
        setError('No account found. Please create a password to continue.');
        setIsCreatingAccount(true);
      } else if (!isCreatingAccount && err.code === 'auth/wrong-password') {
        setError('Invalid password. Please try again.');
      } else if (isCreatingAccount && err.code === 'auth/email-already-in-use') {
        const token = searchParams.get('token');
        let message = 'This email already has an account. If you used Google before, use Continue with Google.';
        if (token && email) {
          const hint = await checkInviteEmailAccountType(email, token);
          if (hint?.accountType) {
            message = getAccountTypeMessage(hint.accountType, {
              authProviders: hint.authProviders || [],
              context: 'signin',
            });
          }
        }
        setError(message);
        setIsCreatingAccount(false);
      } else if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setError('Invalid password. If this email is a creator or Google account, use Continue with Google or the creator dashboard.');
      } else {
        setError(err.message || 'Invalid email or password');
      }
    }
  };

  const handleGoogleLogin = async () => {
    try {
      clearError();
      
      // Validate token if present (for sign-up flow)
      if (tokenInfo && tokenInfo.tokenType === 'signup') {
        if (!tokenInfo.brandEmail) {
          setError('Invalid sign-up link. Please use the link provided in your invitation email.');
          return;
        }
      }
      
      const userCredential = await signInWithPopup(auth, googleProvider);
      const googleEmail = userCredential.user.email;
      
      // Validate Google email matches token email (if token exists for sign-up)
      if (tokenInfo && tokenInfo.tokenType === 'signup' && tokenInfo.brandEmail) {
        if (googleEmail.toLowerCase() !== tokenInfo.brandEmail.toLowerCase()) {
          setError(`This sign-up link is for ${tokenInfo.brandEmail}. Please sign in with the correct Google account.`);
          await firebaseSignOut(auth);
          return;
        }
      }
      
      // If token was used, mark it as used
      const token = searchParams.get('token');
      if (token && tokenInfo) {
        const tokenType = tokenInfo.tokenType || 'signin';
        await BrandSignInTokenService.markTokenAsUsed(token, tokenType);
      }
      
      const userData = await getUserData(userCredential.user.uid);
      console.log('Brand SignIn: Google login - User data from Firestore:', userData);
      
      if (userData && shouldRedirectToCreatorDashboard(userData.userType)) {
        console.log('Brand SignIn: Google login - User is a creator, redirecting to creator dashboard');
        setError(getDenialMessageFromUserData(userData));
        await firebaseSignOut(auth);
        window.location.href = getCreatorDashboardSignInUrl();
        return;
      }
      
      // For sign-up tokens, create brand user document if it doesn't exist
      if (tokenInfo && tokenInfo.tokenType === 'signup' && !userData) {
        try {
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            email: googleEmail,
            brandName: tokenInfo.brandName || userCredential.user.displayName || '',
            brandEmail: tokenInfo.brandEmail || googleEmail,
            userType: 'client',
            credits: 0,
            walletBalance: 0,
            createdAt: new Date().toISOString()
          });
          console.log('Brand SignIn: Created new brand user document for Google sign-up');
        } catch (firestoreErr) {
          console.error('Error creating brand user document:', firestoreErr);
          // Continue anyway
        }
      }
      
      if (userData && !isBrandDashboardAllowedUserType(userData.userType)) {
        console.log('Brand SignIn: Google login - Account type not allowed:', userData.userType);
        setError(getDenialMessageFromUserData(userData));
        await firebaseSignOut(auth);
        return;
      }

      console.log('Brand SignIn: Google login - User allowed for brand dashboard');
      navigate('/dashboard');
      
      // Redirect or perform any other action after successful Google login
    } catch (err) {
      console.error('Brand SignIn: Google login failed:', err);
      
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled. Please try again.');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Popup was blocked. Please allow popups and try again.');
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email. Please sign in with email and password instead.');
      } else if (tokenInfo && tokenInfo.tokenType === 'signup') {
        setError('Failed to sign up with Google. Please try using email and password instead.');
      } else {
        setError(err.message || 'Google sign-in failed. Please try again.');
      }
    }
  };

  return (
    <div className="brand-signin-container">
      {/* Logo */}
      <div className="brand-logo">
        <img src="/ugc3-logo.png" alt="UGC3 Logo" className="logo" />
      </div>

      <div className="signin-form">
        {tokenInfo ? (
          <>
            <h2>Welcome, {tokenInfo.brandName}!</h2>
            <p>You've been invited to sign in and complete your onboarding. Please sign in with your email and password to continue.</p>
          </>
        ) : (
          <>
            <h2>You're back!</h2>
            <p>Let's get you right back to managing your content projects and connect with top creators as a brand or agency.</p>
          </>
        )}
        <p>Oops! <a href="https://ugc3-creator-dasboard.web.app/signin" className="pink-link">I'm a creator</a></p>

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Login Form */}
        <form onSubmit={handleEmailLogin}>
          <label htmlFor="email">Email *</label>
          <input 
            type="email" 
            id="email" 
            placeholder="your@email.com" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            required 
            disabled={!!tokenInfo}
          />

          <label htmlFor="password">Password *</label>
          <input 
            type="password" 
            id="password" 
            placeholder={isCreatingAccount ? "Create a password (min 6 characters)" : "Password"} 
            value={password} 
            onChange={(e) => setPassword(e.target.value)} 
            required 
          />

          {isCreatingAccount && (
            <>
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input 
                type="password" 
                id="confirmPassword" 
                placeholder="Confirm your password" 
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)} 
                required 
              />
            </>
          )}
          
          {/* Forgot Password link above the login button - only show if not creating account */}
          {!isCreatingAccount && (
            <Link to="/forgot-password" className="forgot-password">Forgot Password?</Link>
          )}

          <button type="submit" className="cta-btn">
            {isCreatingAccount ? 'Create Account & Continue' : 'Log In'}
          </button>

          {tokenInfo && !isCreatingAccount && (
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#666', textAlign: 'center' }}>
              Don't have an account yet?{' '}
              <button 
                type="button"
                onClick={() => setIsCreatingAccount(true)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#f8049c', 
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0
                }}
              >
                Create a password
              </button>
            </p>
          )}
        </form>

        {/* Google Login Button */}
        <button className="google-login-btn" onClick={handleGoogleLogin}>
          Login with Google
        </button>

        <p>Don't have an account? <a href={window.location.hostname === 'localhost' ? 'http://localhost:3000/brand-signup' : 'https://ugc3.co.uk/brand-signup'} style={{ color: '#f8049c', textDecoration: 'none' }}>Let's chat so we can take your UGC to the next level</a></p>
      </div>
    </div>
  );
};

export default BrandSignIn;
